import type { ReactNode } from 'react';
import { NO_VALUE } from '../format.ts';
import { unread, whyUnread, type Read } from './read.ts';
import styles from './states.module.css';

/* ==========================================================================
   A ZERO IS A MEASUREMENT. AN UNREAD SOURCE IS NOT.

   The rendering half of `unread` / `figure` / `whyUnread` (read.ts, where the
   argument for all of this is written out in full). This is the component a
   surface reaches for, and it is deliberately the least clever thing in the
   module: it takes the read the figure was derived from, and prints the
   figure only if at least one of those sources has actually answered.

     <Figure of={read} value={rows.length} />        →  28    /  —
     Queue (<Figure of={read} value={open} />)       →  (28)  /  (—)
     <Figure of={read} value={pct} unit="%" />       →  55.4% /  —

   and, in a prop position that wants a plain value rather than a node:

     <Panel count={figure(read, rows.length)} … />

   It renders a bare text node, not a box: it has to be droppable inside an
   existing panel title, an existing footer chip and an existing KPI cell
   without changing any of their type, spacing or alignment, because the whole
   point is that a degraded screen keeps the SHAPE of the healthy one and only
   its values give way.
   ========================================================================== */

export function Figure({
  of,
  value,
  unit,
  children,
}: {
  /** The read (or reads) this figure was derived from. */
  of: Read | readonly Read[];
  /**
   * The derived figure. `null`/`undefined` blanks it too — a mean over
   * nothing is not zero either.
   */
  value?: number | string | null | undefined;
  /** Printed dimmer, immediately after the value, inside the same cell. */
  unit?: string | undefined;
  /** Alternative to `value` for a formatted node. Blanked on the same test. */
  children?: ReactNode;
}) {
  const blank = unread(of) || (children === undefined && (value === null || value === undefined));
  if (blank) {
    return (
      <span className={styles.figureUnread} title={whyUnread(of)}>
        {NO_VALUE}
      </span>
    );
  }
  return (
    <>
      {children ?? value}
      {unit !== undefined && <span className={styles.figureUnit}>{unit}</span>}
    </>
  );
}
